import React, { useState, useEffect } from 'react';
import TreatmentPlanDemoChat from './TreatmentPlanDemoChat';
import TreatmentPlanPDFViewer from './TreatmentPlanPDFViewer';
import { ChatMessage, ViewMode } from '../types';
import { TreatmentPlanProvider, useTreatmentPlan } from '../contexts/TreatmentPlanContext';
import { Paperclip } from 'lucide-react';

const TreatmentPlanDemoContent: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('split');
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [currentStep, setCurrentStep] = useState<'initial' | 'diagnosis' | 'criteria' | 'generation' | 'complete'>('initial');
  const { setCurrentTreatmentPlan } = useTreatmentPlan();

  // Script messages - define before useEffect to avoid dependency issues
  const scriptMessages: ChatMessage[] = [
    {
      id: '1',
      role: 'user',
      content: 'Uploaded patient scan',
      timestamp: new Date(),
      images: [{
        name: 'patient-scan.jpg',
        data: '/demo-patient-photo.jpg'
      }]
    },
    {
      id: '2',
      role: 'assistant',
      content: 'I\'ve received the patient scan. Let me analyze the orthodontic features and identify the key diagnostic criteria.\n\n🔍 **Analyzing image...**',
      timestamp: new Date()
    },
    {
      id: '3',
      role: 'assistant',
      content: '## OrthoVision Analysis\n\nBased on my analysis of the uploaded images, here are the key findings:\n\n### Sagittal Analysis\n- **Molar Relationship**: Class I on both sides\n- **Overjet**: 4mm (slightly increased)\n- **Overbite**: 40% (normal range)\n\n### Transverse Analysis\n- **Midlines**: Upper midline shifted 2mm to the right\n- **Arch Form**: Ovoid arch form, well-coordinated\n- **Crossbite**: No posterior crossbite present\n\n### Vertical Analysis\n- **Facial Pattern**: Mesofacial\n- **Smile Arc**: Consonant\n- **Gingival Display**: Normal (1-2mm)\n\n### Space Analysis\n- **Crowding**: Moderate crowding in lower anterior (4-5mm)\n- **Upper Arch**: Mild crowding (2-3mm)\n- **Bolton Discrepancy**: Within normal limits\n\n### Other Findings\n- **Third Molars**: Present, developing normally\n- **TMJ**: No apparent dysfunction\n- **Periodontal**: Healthy gingival tissues\n\nWould you like me to proceed with developing treatment options based on these findings?',
      timestamp: new Date(),
      action: 'vision_complete'
    },
    {
      id: '4',
      role: 'user',
      content: 'Yes, please develop treatment options',
      timestamp: new Date()
    },
    {
      id: '5',
      role: 'assistant',
      content: 'I\'ll now develop comprehensive treatment options based on the diagnostic findings. Let me analyze the best approaches for addressing the moderate lower crowding and upper midline deviation.\n\n⚙️ **Generating treatment options...**',
      timestamp: new Date()
    },
    {
      id: '6',
      role: 'assistant',
      content: '## Treatment Options\n\nBased on the analysis, I\'ve developed three treatment approaches:\n\n### Option 1: Non-Extraction with IPR\n**Duration**: 18-20 months\n**Approach**: Address crowding through interproximal reduction and expansion\n- Resolve lower crowding with 3-4mm IPR\n- Align upper midline with asymmetric mechanics\n- Maintain Class I relationship\n\n**Pros**: ✓ Preserves all teeth ✓ Shorter treatment time ✓ Maintains profile\n**Cons**: ✗ Limited space gain ✗ Potential for relapse\n\n### Option 2: Lower Incisor Extraction\n**Duration**: 20-24 months\n**Approach**: Extract one lower incisor to resolve crowding\n- Extract lower incisor (preferably central)\n- Comprehensive alignment with fixed appliances\n- Midline correction with elastics\n\n**Pros**: ✓ Definitive space solution ✓ Stable long-term result\n**Cons**: ✗ Requires extraction ✗ Longer treatment ✗ Bolton consideration\n\n### Option 3: Clear Aligner Therapy\n**Duration**: 12-14 months\n**Approach**: Invisalign comprehensive package\n- Sequential expansion and alignment\n- IPR as needed (2-3mm lower)\n- Midline correction with precision cuts\n\n**Pros**: ✓ Aesthetic option ✓ Removable ✓ Good for mild-moderate cases\n**Cons**: ✗ Compliance dependent ✗ Higher cost ✗ Limited control\n\nWhich option would you like to proceed with?',
      timestamp: new Date(),
      action: 'criteria_complete_with_options'
    },
    {
      id: '7',
      role: 'user',
      content: 'Let\'s go with Option 1 - Non-extraction with IPR',
      timestamp: new Date()
    },
    {
      id: '8',
      role: 'assistant',
      content: '## 🎯 Generating Detailed Treatment Plan\n\nExcellent choice! The non-extraction approach with IPR is well-suited for this case. I\'m now generating a comprehensive treatment plan with:\n\n- Detailed phase-by-phase treatment sequence\n- Specific IPR protocol and locations\n- Midline correction mechanics\n- Retention protocol\n- Appointment scheduling recommendations\n\n⏳ **Generating comprehensive treatment plan...**\n\nThis will take a moment as I create the complete clinical protocol.',
      timestamp: new Date(),
      action: 'treatment_plan_generated'
    }
  ];

  // Track when to advance steps
  useEffect(() => {
    // Advance steps based on message index
    if (currentMessageIndex === 0) {
      setCurrentStep('initial');
    } else if (currentMessageIndex === 2) {
      setCurrentStep('diagnosis');
    } else if (currentMessageIndex === 4) {
      setCurrentStep('criteria');
    } else if (currentMessageIndex === 6) {
      setCurrentStep('generation');
      // Generate initial treatment plan
      setTimeout(() => {
        const initialPlan = {
          id: 'demo-plan-' + Date.now(),
          title: 'Treatment Plan - ' + new Date().toLocaleDateString(),
          content: 'diagnosis',
          createdAt: new Date(),
          sessionId: 'demo-session'
        };
        setCurrentTreatmentPlan(initialPlan);
      }, 100);
    } else if (currentMessageIndex >= scriptMessages.length) {
      // Wait a bit after the last message before showing complete status
      setTimeout(() => {
        setCurrentStep('complete');
        // Update with full treatment plan
        const completePlan = {
          id: 'demo-plan-' + Date.now(),
          title: 'Treatment Plan - ' + new Date().toLocaleDateString(),
          content: 'complete',
          createdAt: new Date(),
          sessionId: 'demo-session'
        };
        setCurrentTreatmentPlan(completePlan);
      }, 200);
    }
  }, [currentMessageIndex, setCurrentTreatmentPlan, scriptMessages.length]);



  // Simulate typing animation
  useEffect(() => {
    if (currentMessageIndex < scriptMessages.length) {
      const timer = setTimeout(() => {
        if (currentMessageIndex > 0) {
          // Add typing indicator before assistant messages
          if (scriptMessages[currentMessageIndex].role === 'assistant') {
            setIsLoading(true);
            setTimeout(() => {
              setMessages(prev => {
                // Check if message already exists to prevent duplicates
                const messageExists = prev.some(msg => msg.id === scriptMessages[currentMessageIndex].id);
                if (messageExists) return prev;
                return [...prev, scriptMessages[currentMessageIndex]];
              });
              setCurrentMessageIndex(prev => prev + 1);
              setIsLoading(false);
            }, 300);
          } else {
            setMessages(prev => {
              // Check if message already exists to prevent duplicates
              const messageExists = prev.some(msg => msg.id === scriptMessages[currentMessageIndex].id);
              if (messageExists) return prev;
              return [...prev, scriptMessages[currentMessageIndex]];
            });
            setCurrentMessageIndex(prev => prev + 1);
          }
        } else {
          // First message doesn't need typing indicator
          setMessages([scriptMessages[0]]);
          setCurrentMessageIndex(1);
        }
      }, currentMessageIndex === 0 ? 100 : 400);
      
      return () => clearTimeout(timer);
    }
  }, [currentMessageIndex, scriptMessages]);


  return (
    <div className="h-full flex relative overflow-hidden bg-medical-50">
          {/* Chat Interface */}
          {(viewMode === 'chat' || viewMode === 'split') && (
            <div className={`${viewMode === 'split' ? 'w-1/2' : 'w-full'} h-full flex flex-col`}>
              <TreatmentPlanDemoChat
                messages={messages}
                isLoading={isLoading}
              />
              {/* Input area (disabled) */}
              <div className="bg-white border-t border-medical-200 p-4">
                <div className="flex items-center space-x-3">
                  {/* Paperclip button */}
                  <button
                    type="button"
                    disabled
                    className="p-2 text-medical-400 hover:text-medical-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Upload image (disabled in demo)"
                  >
                    <Paperclip className="w-5 h-5" />
                  </button>
                  
                  {/* Text input */}
                  <input
                    type="text"
                    placeholder="Type a message..."
                    disabled
                    className="flex-1 px-4 py-2 bg-medical-50 rounded-lg text-medical-500 cursor-not-allowed"
                  />
                  
                  {/* Send button could be added here if needed */}
                </div>
              </div>
            </div>
          )}

          {/* PDF Viewer */}
          {(viewMode === 'document' || viewMode === 'split') && (
            <div className={`${viewMode === 'split' ? 'w-1/2 border-l border-medical-200' : 'w-full'} h-full`}>
              <TreatmentPlanPDFViewer 
                currentStep={currentStep}
                showExportButton={currentStep === 'complete'}
              />
            </div>
          )}
    </div>
  );
};

const TreatmentPlanDemo: React.FC<{ onBack?: () => void; onClose?: () => void }> = ({ onBack, onClose }) => {
  // If onBack is provided, we need to handle navigation differently
  // For now, we'll use the full app experience
  
  useEffect(() => {
    // If user wants to go back, they can use the sidebar navigation
    if (onBack) {
      // Store the onBack function somewhere if needed
    }
  }, [onBack]);

  return (
    <TreatmentPlanProvider>
      <TreatmentPlanDemoContent />
    </TreatmentPlanProvider>
  );
};

export default TreatmentPlanDemo;